import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, getToken } from '../lib/api';
import { useClients } from '../lib/lookups';

interface ReportRow { [k: string]: unknown }
interface ReportResp { mode: string; total: number; page: number; pageSize: number; rows: ReportRow[] }

const PAGE_SIZE = 50;

// Aggregate modes have no free-text or product filter to apply — they group
// rather than list. They still honour dates and the client filter.
const AGGREGATE_MODES = ['stage-delays', 'crm-missed-pct', 'crm-dod-monthly', 'design-kpi'];

const REPORT_MODES = [
  { value: 'job', label: 'Job Orders Report', desc: 'Full job spine with client, product, sheet, paper, finish, machine.' },
  { value: 'po-intake', label: "Today's PO Intake (value)", desc: 'Purchase orders by PO Date in the date range — quantity, rate, amount and value including GST, with a TOTAL row. Defaults to today.' },
  { value: 'po', label: 'Purchase Orders', desc: 'Listing of purchase orders and status.' },
  { value: 'product', label: 'Product Master', desc: 'Details of product definitions.' },
  { value: 'die', label: 'Die Design', desc: 'Design dimensions, layout, ups, and vendors.' },
  { value: 'wip', label: 'WIP Track Records', desc: 'Work-in-progress inventory status and balances.' },
  { value: 'missed-to-set', label: 'DOD Missed to Set', desc: 'POs missing a DOD where the customer requested date has passed.' },
  { value: 'dispatched-late', label: 'Dispatched Late', desc: 'Jobs dispatched past their DOD.' },
  { value: 'dispatched-no-promise', label: 'Dispatched (No DOD)', desc: 'Jobs dispatched without any registered DOD.' },
  { value: 'overall-missed', label: 'Overall Delivery Missed', desc: 'Aggregate of late dispatches and pending POs past their DOD/requested dates.' },
  { value: 'dod-revisions', label: 'DOD Revision Log', desc: 'Audit history list of changes made to DODs.' },
  { value: 'crm-dod-monthly', label: 'CRM DOD Performance (monthly)', desc: 'Per CRM: dispatched, met, missed, pending and Met % — with a month-by-month breakdown under each total.' },
  { value: 'crm-missed-pct', label: 'CRM Missed Percentage', desc: 'Performance report per CRM user showing total POs, promised %, met/missed counts, and late %.' },
  { value: 'dod-today', label: 'DOD Set for Today', desc: 'POs where the promised Date of Dispatch is today.' },
  { value: 'dod-missed-pending', label: 'DOD Missed (Not Dispatched)', desc: 'POs that have missed their DOD and are not yet dispatched.' },
  { value: 'pending-overdue', label: 'Pending Overdue', desc: 'Undispatched POs past their DOD.' },
  { value: 'dispatched-one-day-late', label: 'Dispatched 1 Day Late', desc: 'Dispatches that were completed exactly 1 day late.' },
  { value: 'fms', label: 'FMS Lifecycle Sheet', desc: 'Unified grid showing PO date, promise date, design/board status, job card date, dispatch date, and outcome.' },
  { value: 'stage-delays', label: 'Stage Delay Metrics', desc: 'Average and max delays across CRM entry, Design, Board, and Dispatch stages.' },
  { value: 'produced-not-dispatched', label: 'Produced, Not Dispatched', desc: 'Job cards dispatched for less than was produced: PO qty, job card qty (first issue and reissues together), client qty, produced, dispatched, scrapped and the boxes left over, biggest first. A PO dispatched in full is left out. Dates go by the last dispatch. "Check" flags production over 150% of the client qty, and jobs closed with boxes left.' },
  { value: 'job-reissue', label: 'Job Reissue Report', desc: 'Reissued job cards: the boxes before, the reissue - its boxes, sheets (actual and wastage) and what was produced after it - with the reason, who and when.' },
  { value: 'design-kpi', label: 'Design KPI', desc: 'Performance report per Designer showing total jobs, met, missed, and SLA met % based on the 8 hour Turnaround Time.' },
];

const getLocalDateString = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const localDate = new Date(d.getTime() - (offset * 60 * 1000));
  return localDate.toISOString().slice(0, 10);
};

export function ReportsPage() {
  const today = getLocalDateString();
  const [mode, setMode] = useState('job');
  const [q, setQ] = useState('');
  const [product, setProduct] = useState('');
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [applied, setApplied] = useState(false);

  const clients = useClients();

  const selectedModeInfo = REPORT_MODES.find(m => m.value === mode) ?? {
    value: 'job',
    label: 'Job Orders Report',
    desc: 'Full job spine with client, product, sheet, paper, finish, machine.'
  };

  function buildParams(): URLSearchParams {
    const sp = new URLSearchParams({ mode, page: String(page), pageSize: String(PAGE_SIZE) });
    if (q) sp.set('q', q);
    if (product) sp.set('product', product);
    if (from) sp.set('from', from);
    if (to) sp.set('to', to);
    if (clientIds.length) sp.set('clients', clientIds.join(','));
    return sp;
  }

  const report = useQuery({
    enabled: applied,
    queryKey: ['reports', { mode, q, product, from, to, clientIds, page }],
    queryFn: () => api<ReportResp>(`/api/reports?${buildParams().toString()}`),
  });

  function apply(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setApplied(true);
  }

  function handleModeChange(newMode: string) {
    setMode(newMode);
    setApplied(false);
    setPage(1);
  }

  const [exporting, setExporting] = useState(false);

  function exportAs(ext: 'csv' | 'xlsx') {
    const sp = new URLSearchParams({ mode });
    if (q) sp.set('q', q);
    if (product) sp.set('product', product);
    if (from) sp.set('from', from);
    if (to) sp.set('to', to);
    if (clientIds.length) sp.set('clients', clientIds.join(','));
    const token = getToken();
    setExporting(true);
    fetch(`/api/reports/export.${ext}?${sp.toString()}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    }).then(async (r) => {
      if (!r.ok) {
        const msg = await r.text().catch(() => '');
        throw new Error(msg || `Export failed (${r.status})`);
      }
      return r.blob();
    }).then((blob) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${mode}-report-${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    }).catch((e: Error) => {
      alert(`Export failed: ${e.message}`);
    }).finally(() => setExporting(false));
  }

  const rows = report.data?.rows ?? [];
  const total = report.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const headers = rows[0] ? Object.keys(rows[0]) : [];

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-xl font-semibold text-brand">{selectedModeInfo.label}</h1>
          <p className="text-xs text-slate-500">{selectedModeInfo.desc}</p>
        </div>
        <div className="min-w-64">
          <label className="label text-slate-400 text-[10px] uppercase font-bold">Select Report Mode</label>
          <select
            className="input text-xs font-semibold py-1.5 h-9 bg-white cursor-pointer"
            value={mode}
            onChange={(e) => handleModeChange(e.target.value)}
          >
            {REPORT_MODES.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      <form className="card" onSubmit={apply}>
        <div className="card-header">Filter Parameters</div>
        <div className="card-body grid gap-3 md:grid-cols-4">
          {!AGGREGATE_MODES.includes(mode) && (
            <div className="md:col-span-2">
              <label className="label">Search (Job ID, PO No, Job Name)</label>
              <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Free-text search…" />
            </div>
          )}
          <div>
            <label className="label">From Date</label>
            <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">To Date</label>
            <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>

          {!AGGREGATE_MODES.includes(mode) && (
            <div className="md:col-span-4">
              <label className="label">Product (name or Product ID)</label>
              <input className="input" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="e.g. REKO-25 or PRU15586" />
            </div>
          )}

          {mode !== 'stage-delays' && mode !== 'crm-missed-pct' && mode !== 'design-kpi' && (
            <div className="md:col-span-4">
              <label className="label">Clients (leave empty for all)</label>
              <div className="max-h-32 overflow-auto rounded border border-slate-200 p-2 grid gap-1 sm:grid-cols-3 lg:grid-cols-4 text-xs">
                {clients.data?.map((c) => (
                  <label key={c.id} className="flex items-center gap-2">
                    <input type="checkbox"
                      checked={clientIds.includes(c.id)}
                      onChange={(e) => {
                        setClientIds((cur) =>
                          e.target.checked ? [...cur, c.id] : cur.filter((x) => x !== c.id),
                        );
                      }} />
                    <span className="truncate" title={c.name}>{c.name}</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-500">{clientIds.length} selected</p>
            </div>
          )}

          <div className="md:col-span-4 flex flex-wrap items-center gap-2 pt-2">
            <button type="submit" className="btn-primary">Run report</button>
            {applied && (
              <>
                <button type="button" className="btn-primary bg-emerald-600 hover:bg-emerald-700 text-white" disabled={exporting} onClick={() => exportAs('xlsx')}>
                  {exporting ? 'Exporting…' : 'Export Excel'}
                </button>
                <button type="button" className="btn-ghost" disabled={exporting} onClick={() => exportAs('csv')}>
                  {exporting ? 'Exporting…' : 'Export CSV'}
                </button>
              </>
            )}
            <button type="button" className="btn-ghost text-xs" onClick={() => { setFrom(today); setTo(today); }}>Set to Today</button>
            {(from || to) && <button type="button" className="btn-ghost text-xs text-red-500" onClick={() => { setFrom(''); setTo(''); }}>Clear Dates</button>}
          </div>
        </div>
      </form>

      {applied && (
        <div className="card overflow-hidden">
          <div className="card-header flex justify-between">
            <span>Results</span>
            <span className="text-xs font-normal">{total.toLocaleString()} rows</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
                <tr>{headers.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
              </thead>
              <tbody>
                {report.isLoading && <tr><td colSpan={headers.length || 1} className="p-6 text-center text-slate-500">Loading…</td></tr>}
                {report.isError && <tr><td colSpan={headers.length || 1} className="p-6 text-center text-red-600">Failed: {(report.error as Error).message}</td></tr>}
                {!report.isLoading && rows.length === 0 && <tr><td colSpan={headers.length || 1} className="p-6 text-center text-slate-500">No records found.</td></tr>}
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                    {headers.map((h) => (
                      <td key={h} className="px-3 py-2 text-slate-700 text-xs">
                        {formatVal(r[h])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex justify-between items-center p-3 text-sm border-t border-slate-200">
              <div className="text-slate-500">Page {page} of {totalPages.toLocaleString()}</div>
              <div className="flex gap-2">
                <button className="btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
                <button className="btn-ghost" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatVal(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
    return v;
  }
  return String(v);
}
